import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import { apiClient, ApiClientError, getRecentApiErrors } from '@/api/client'
import {
  MAX_FILES_PER_MESSAGE,
  attachmentKind,
  pastedImageName,
  precheckFile,
  prepareForUpload,
  type AttachmentKind,
} from '@/lib/aiAttachments'
import type {
  AIActionCard,
  AIActionCardOpen,
  AIAttachment,
  AIConversationMessage,
  AIPostMessageResult,
} from '@/types/ai'
import { CHANGED_STATUSES, cardErrorOutcome, toChatCard, type CardOp, type ChatActionCard } from '@/lib/aiActionCard'
import { invalidateActionDomains } from '@/lib/aiActionDomains'
import { speakOnDevice } from '@/lib/aiDeviceVoice'
import type { UiActionContext, UiActionRefusal } from '@/lib/aiNavTargets'
import { knownRoutesFromRouter, runUiAction } from '@/lib/aiUiActions'
import { desktop } from '@/lib/printTransport'
import { PENDING_LABELS, speakableText, type ChatAttachment, type ChatMessage } from './chatRows'

// ── Bhookly AI chat engine ──────────────────────────────────────────────────
// Everything the copilot does with data, in one hook shared by the bubble
// (BhooklyAI.tsx) and the full page (BhooklyAIPage.tsx): the conversation and
// its resume, sending a turn and running its ui_action, confirm cards
// (confirm / undo / dismiss), thumbs, problem reports, voice in
// (mic → /ai/transcribe), read-aloud (/ai/tts) and attached files
// (/ai/attachments, F-AI-62).
//
// The two surfaces share the stored conversation id, so the page continues
// the chat the bubble started and vice versa.

export interface Capabilities {
  voiceIn: boolean
  voiceOut: boolean
  report: boolean
  /** Attaching files; off when the backend says uploads are turned off. */
  files: boolean
}

/** A file in the composer, waiting for the next message. */
export interface ComposerFile {
  localId: string
  name: string
  kind: AttachmentKind
  size: number
  status: 'uploading' | 'ready' | 'error'
  /** Upload progress, 0..1. */
  progress: number
  error?: string
  /** The server id once uploaded. */
  serverId?: string
  pages?: number
  /** An image's preview on this device. */
  previewUrl?: string
}

export interface CopilotChatOptions {
  role: string
  userId: string
  flags: string[]
  /** Called when the backend says the copilot is off for this user. */
  onHide: () => void
  caps: Capabilities
  setCaps: (patch: Partial<Capabilities>) => void
}

export interface VoiceInput {
  isRecording: boolean
  recordingSeconds: number
  transcribing: boolean
  micError: string | null
  /** 0..1 mic level while recording, for the voice-mode orb. */
  level: number
  startRecording: () => Promise<void>
  stopRecording: () => void
}

export interface Speech {
  voiceState: { id: string; status: 'loading' | 'playing' } | null
  onSpeak: (m: ChatMessage) => void
  stopPlayback: () => void
}

export interface CopilotChat {
  messages: ChatMessage[]
  input: string
  setInput: (v: string) => void
  sending: boolean
  canSend: boolean
  reportMode: boolean
  setReportMode: (v: boolean) => void
  pendingLabel: string
  inputRef: RefObject<HTMLTextAreaElement>
  scrollRef: RefObject<HTMLDivElement>
  focusInput: () => void
  /** The server conversation id, once one exists. */
  conversationId: string | null
  send: (text: string) => Promise<void>
  /** Sends `input`, or the report when in report mode. */
  submit: () => void
  /** Puts text in the composer without sending. */
  seed: (text: string) => void
  newChat: () => void
  /** Loads a past conversation's transcript and continues it. */
  openConversation: (id: string) => Promise<void>
  onFeedback: (m: ChatMessage, thumbs: 1 | -1) => void
  /** Confirms, undoes or dismisses one of a reply's cards. */
  actOnCard: (messageId: string, cardId: string, op: CardOp) => Promise<void>
  /** Opens the screen a card points at; resolves to a note when it can't. */
  openCardScreen: (open: AIActionCardOpen) => Promise<string | null>
  voice: VoiceInput
  speech: Speech
  /**
   * Voice mode: a transcript is sent as soon as it lands and the reply is
   * read aloud. Off, the mic fills the composer and replies stay silent.
   */
  autoVoice: boolean
  setAutoVoice: (on: boolean) => void
  /** Files waiting for the next message. */
  files: ComposerFile[]
  /** Adds picked, pasted or dropped files (each uploads straight away). */
  addFiles: (list: File[]) => void
  removeFile: (localId: string) => void
}

/** Only a recent conversation is resumed; older chats start fresh. */
const CONV_MAX_AGE_MS = 12 * 60 * 60 * 1000
/** Hard stop for a single voice recording. */
const MAX_RECORDING_SECONDS = 60
/** 403 codes that mean "this user has no copilot": hide the whole surface. */
const HIDE_CODES = new Set(['ai_disabled', 'ai_role_not_enabled'])

let idSeq = 0
const nextId = () => `m${Date.now()}_${idSeq++}`

const conversationStorageKey = (userId: string) => `bhookly_ai_conversation:${userId || 'unknown'}`

function readStoredConversation(userId: string): string | null {
  try {
    return localStorage.getItem(conversationStorageKey(userId))
  } catch {
    return null
  }
}

function writeStoredConversation(userId: string, id: string | null): void {
  try {
    if (id) localStorage.setItem(conversationStorageKey(userId), id)
    else localStorage.removeItem(conversationStorageKey(userId))
  } catch {
    /* private mode / storage disabled — the chat just won't resume */
  }
}

export function errorCode(e: unknown): string | undefined {
  return e instanceof ApiClientError ? e.code : undefined
}

export function isHideCode(code: string | undefined): boolean {
  return !!code && HIDE_CODES.has(code)
}

/** The current URL's search params as plain strings, for the turn request. */
function currentSearch(): Record<string, string> {
  const out: Record<string, string> = {}
  new URLSearchParams(window.location.search).forEach((value, key) => {
    out[key] = value
  })
  return out
}

/** What the staff member reads when a turn fails. */
function turnErrorText(code: string | undefined): string {
  switch (code) {
    case 'ai_turn_in_progress':
      return 'Still answering your last message — send again in a moment.'
    case 'ai_unavailable':
      return 'Bhookly AI is unavailable right now — please try again in a little while.'
    case 'ai_step_failed':
      return 'Something went wrong answering that — please try again.'
    case 'ai_attachment_unavailable':
      return 'One of the files is no longer available — attach it again.'
    case 'ai_attachments_disabled':
      return 'Attaching files is turned off for this restaurant.'
    default:
      return "Sorry — I couldn't answer that just now. Please try again in a moment."
  }
}

/** The note appended to a reply whose screen could not be opened (plan Appendix E). */
function refusalNote(reason: UiActionRefusal | 'navigation_failed'): string {
  return reason === 'role' ? "(That screen isn't available for your role)" : "(I couldn't open that screen)"
}

async function appVersion(): Promise<string> {
  try {
    const v = await desktop()?.appVersion()
    if (v) return `desktop ${v}`
  } catch {
    /* fall through to the web build */
  }
  const env = import.meta.env?.VITE_APP_VERSION
  return typeof env === 'string' && env ? env : 'web'
}

/** A turn's cards as the chat holds them, with the server-clock offset of this copy. */
function chatCards(cards: AIActionCard[] | undefined, receivedAt: number): ChatActionCard[] | undefined {
  return cards?.length ? cards.map((c) => toChatCard(c, receivedAt)) : undefined
}

function chatAttachments(list: AIAttachment[] | undefined): ChatAttachment[] | undefined {
  return list?.length
    ? list.map((a) => ({ id: a.id, name: a.name, kind: a.kind, sizeBytes: a.size_bytes, pages: a.pages }))
    : undefined
}

function restoredMessages(list: AIConversationMessage[] | undefined): ChatMessage[] {
  // A stored ui_action is history: it is never re-run on a reload. Cards come
  // back in their current state and are only shown — never confirmed.
  const receivedAt = Date.now()
  return (list ?? [])
    .filter(
      (m) =>
        (m.role === 'user' || m.role === 'assistant') &&
        (m.content.trim() || m.actions?.length || m.attachments?.length),
    )
    .map<ChatMessage>((m) => ({
      id: `s_${m.id}`,
      role: m.role,
      content: m.content,
      serverId: m.role === 'assistant' ? m.id : undefined,
      actions: m.role === 'assistant' ? chatCards(m.actions, receivedAt) : undefined,
      attachments: m.role === 'user' ? chatAttachments(m.attachments) : undefined,
    }))
}

export function useCopilotChat({ role, userId, flags, onHide, caps, setCaps }: CopilotChatOptions): CopilotChat {
  const router = useRouter()
  const queryClient = useQueryClient()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  // Read by the voice hand-off, which runs after a recording, outside render.
  const inputValueRef = useRef('')
  const sendingRef = useRef(false)
  useEffect(() => {
    inputValueRef.current = input
    sendingRef.current = sending
  }, [input, sending])
  const [reportMode, setReportMode] = useState(false)
  const [conversationId, setConversationId] = useState<string | null>(null)
  const convIdRef = useRef<string | null>(null)
  const setConvId = useCallback((id: string | null) => {
    convIdRef.current = id
    setConversationId(id)
  }, [])
  // The transcript reload in flight, if any: a message sent before it lands
  // waits for it, so the turn continues the resumed conversation. "New chat"
  // supersedes it, so a late reload cannot splice the old chat back in.
  const resumeRef = useRef<Promise<void> | null>(null)
  const resumeSupersededRef = useRef(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  // A reply that lands after the surface closed must not navigate the screen
  // out from under the staff member.
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const [autoVoice, setAutoVoiceState] = useState(false)
  const autoVoiceRef = useRef(false)
  const setAutoVoice = useCallback((on: boolean) => {
    autoVoiceRef.current = on
    setAutoVoiceState(on)
  }, [])

  const patchMessage = useCallback((id: string, patch: Partial<ChatMessage>) => {
    setMessages((m) => m.map((msg) => (msg.id === id ? { ...msg, ...patch } : msg)))
  }, [])

  const patchCard = useCallback((messageId: string, cardId: string, patch: Partial<ChatActionCard>) => {
    setMessages((m) =>
      m.map((msg) =>
        msg.id === messageId && msg.actions
          ? { ...msg, actions: msg.actions.map((c) => (c.id === cardId ? { ...c, ...patch } : c)) }
          : msg,
      ),
    )
  }, [])

  /** What the nav registry checks a screen against: role, flags, the router's routes. */
  const uiActionContext = useCallback(
    (): UiActionContext => ({ role, flags, knownRoutes: knownRoutesFromRouter(router) }),
    [role, flags, router],
  )

  const focusInput = useCallback(() => {
    requestAnimationFrame(() => {
      const el = inputRef.current
      if (!el) return
      el.focus()
      el.style.height = 'auto'
      el.style.height = `${Math.min(el.scrollHeight, 140)}px`
    })
  }, [])

  useEffect(() => {
    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
    })
  }, [messages])

  const [pendingLabelIdx, setPendingLabelIdx] = useState(0)
  useEffect(() => {
    if (!sending) {
      setPendingLabelIdx(0)
      return
    }
    const id = window.setInterval(() => setPendingLabelIdx((i) => (i + 1) % PENDING_LABELS.length), 1500)
    return () => window.clearInterval(id)
  }, [sending])

  /** A 403 that switches the copilot off for this user hides the whole surface. */
  const hideIfDisabled = useCallback(
    (e: unknown): boolean => {
      if (isHideCode(errorCode(e))) {
        onHide()
        return true
      }
      return false
    },
    [onHide],
  )

  // ── Resume the stored conversation (transcript lives server-side) ─────────
  useEffect(() => {
    const stored = readStoredConversation(userId)
    if (!stored) return
    let cancelled = false
    resumeRef.current = apiClient
      .aiGetConversation(stored)
      .then((res) => {
        if (cancelled || resumeSupersededRef.current) return
        const conv = res.data?.conversation
        const fresh =
          !!conv &&
          conv.status === 'active' &&
          !!conv.last_message_at &&
          Date.now() - new Date(conv.last_message_at).getTime() < CONV_MAX_AGE_MS
        if (!fresh) {
          writeStoredConversation(userId, null)
          return
        }
        setConvId(stored)
        const restored = restoredMessages(res.data?.messages)
        if (!restored.length) return
        setMessages((prev) => [...restored, ...prev])
      })
      .catch((e: unknown) => {
        if (cancelled || hideIfDisabled(e)) return
        // Forget the id only when the server refused it (gone, not ours); a
        // network blip keeps it for the next open.
        const status = e instanceof ApiClientError ? e.status : undefined
        if (status && status >= 400 && status < 500) writeStoredConversation(userId, null)
      })
      .finally(() => {
        if (!cancelled) resumeRef.current = null
      })
    return () => {
      cancelled = true
      resumeRef.current = null
    }
  }, [userId, hideIfDisabled, setConvId])

  const startConversation = useCallback(async (): Promise<string> => {
    const res = await apiClient.aiStartConversation()
    const id = res.data?.id
    if (!id) throw new Error('ai: no conversation id')
    setConvId(id)
    writeStoredConversation(userId, id)
    return id
  }, [userId, setConvId])

  const postTurn = useCallback(
    async (content: string, attachmentIds: string[]): Promise<AIPostMessageResult | undefined> => {
      const body = {
        content,
        route: window.location.pathname,
        search: currentSearch(),
        ...(attachmentIds.length ? { attachment_ids: attachmentIds } : {}),
      }
      const id = convIdRef.current ?? (await startConversation())
      try {
        return (await apiClient.aiPostMessage(id, body)).data
      } catch (e) {
        // The stored conversation is gone (cleaned up, or another device closed
        // it): the turn never ran, so it is safe to replay once on a new one.
        if (errorCode(e) !== 'ai_conversation_not_found') throw e
        setConvId(null)
        const fresh = await startConversation()
        return (await apiClient.aiPostMessage(fresh, body)).data
      }
    },
    [startConversation, setConvId],
  )

  // ── Read aloud: /ai/tts ───────────────────────────────────────────────────
  // Server audio, or — for English with no English server voice — the
  // device's own English voice (never the Urdu voice).
  const [voiceState, setVoiceState] = useState<{ id: string; status: 'loading' | 'playing' } | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const audioUrlRef = useRef<string | null>(null)
  const deviceStopRef = useRef<(() => void) | null>(null)
  const speakSeqRef = useRef(0)

  const stopPlayback = useCallback(() => {
    speakSeqRef.current += 1
    deviceStopRef.current?.()
    deviceStopRef.current = null
    audioRef.current?.pause()
    audioRef.current = null
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current)
      audioUrlRef.current = null
    }
    setVoiceState(null)
  }, [])

  const speak = useCallback(
    (id: string, content: string) => {
      stopPlayback()
      const seq = speakSeqRef.current
      setVoiceState({ id, status: 'loading' })
      apiClient
        .aiSpeak(speakableText(content))
        .then((speech) => {
          if (seq !== speakSeqRef.current || !mountedRef.current) return
          if (speech.kind === 'device') {
            const stop = speakOnDevice(speech.text, speech.lang, {
              start: () => seq === speakSeqRef.current && setVoiceState({ id, status: 'playing' }),
              end: () => seq === speakSeqRef.current && stopPlayback(),
            })
            if (stop) deviceStopRef.current = stop
            else stopPlayback() // no speech on this device (e.g. an Android WebView)
            return
          }
          const url = URL.createObjectURL(speech.audio)
          audioUrlRef.current = url
          const audio = new Audio(url)
          audioRef.current = audio
          audio.onended = () => stopPlayback()
          audio.onerror = () => stopPlayback()
          setVoiceState({ id, status: 'playing' })
          return audio.play()
        })
        .catch((e: unknown) => {
          if (hideIfDisabled(e)) return
          if (errorCode(e) === 'ai_voice_unavailable') setCaps({ voiceOut: false })
          stopPlayback()
        })
    },
    [stopPlayback, hideIfDisabled, setCaps],
  )

  const onSpeak = useCallback(
    (m: ChatMessage) => {
      if (voiceState?.id === m.id) {
        stopPlayback()
        return
      }
      speak(m.id, m.content)
    },
    [voiceState, stopPlayback, speak],
  )

  // ── Attached files: /ai/attachments (F-AI-62) ──────────────────────────────
  // Each file uploads as soon as it is added, so Send only lists the ids.
  const [files, setFiles] = useState<ComposerFile[]>([])
  const filesRef = useRef<ComposerFile[]>([])
  const updateFiles = useCallback((fn: (prev: ComposerFile[]) => ComposerFile[]) => {
    setFiles((prev) => {
      const next = fn(prev)
      filesRef.current = next
      return next
    })
  }, [])
  const patchFile = useCallback(
    (localId: string, patch: Partial<ComposerFile>) =>
      updateFiles((prev) => prev.map((f) => (f.localId === localId ? { ...f, ...patch } : f))),
    [updateFiles],
  )

  const uploadOne = useCallback(
    async (localId: string, file: File) => {
      try {
        const { blob, name } = await prepareForUpload(file)
        const res = await apiClient.aiUploadAttachment(blob, name, (p) => patchFile(localId, { progress: p }))
        const a = res.data
        if (!a) throw new Error('ai: no attachment')
        if (!filesRef.current.some((f) => f.localId === localId)) {
          // Removed while uploading: drop the stored copy too.
          apiClient.aiDeleteAttachment(a.id).catch(() => {})
          return
        }
        patchFile(localId, { status: 'ready', progress: 1, serverId: a.id, name: a.name, size: a.size_bytes, pages: a.pages })
      } catch (e) {
        if (hideIfDisabled(e)) return
        if (errorCode(e) === 'ai_attachments_disabled') {
          setCaps({ files: false })
          updateFiles(() => [])
          return
        }
        const message = e instanceof ApiClientError && e.code?.startsWith('ai_attachment') ? e.message : null
        patchFile(localId, { status: 'error', error: message || 'Couldn’t upload this file — try again.' })
      }
    },
    [patchFile, hideIfDisabled, setCaps, updateFiles],
  )

  const addFiles = useCallback(
    (list: File[]) => {
      if (!caps.files || !list.length) return
      const room = MAX_FILES_PER_MESSAGE - filesRef.current.filter((f) => f.status !== 'error').length
      const added: { file: File; entry: ComposerFile }[] = list.map((file, i) => {
        const name = file.name || pastedImageName(file.type, i)
        const kind = attachmentKind({ name, type: file.type })
        const refused =
          i >= room ? `Attach up to ${MAX_FILES_PER_MESSAGE} files to one message.` : precheckFile(file)
        return {
          file: file.name ? file : new File([file], name, { type: file.type }),
          entry: {
            localId: nextId(),
            name,
            kind,
            size: file.size,
            status: refused ? 'error' : 'uploading',
            progress: 0,
            error: refused ?? undefined,
            previewUrl: kind === 'image' && !refused ? URL.createObjectURL(file) : undefined,
          },
        }
      })
      updateFiles((prev) => [...prev, ...added.map((a) => a.entry)])
      for (const { file, entry } of added) {
        if (entry.status === 'uploading') void uploadOne(entry.localId, file)
      }
      focusInput()
    },
    [caps.files, updateFiles, uploadOne, focusInput],
  )

  const removeFile = useCallback(
    (localId: string) => {
      const f = filesRef.current.find((x) => x.localId === localId)
      if (!f) return
      if (f.serverId) apiClient.aiDeleteAttachment(f.serverId).catch(() => {})
      if (f.previewUrl) URL.revokeObjectURL(f.previewUrl)
      updateFiles((prev) => prev.filter((x) => x.localId !== localId))
    },
    [updateFiles],
  )

  const send = useCallback(
    async (raw: string) => {
      const text = raw.trim()
      const attached = filesRef.current.filter((f) => f.status === 'ready' && f.serverId)
      const uploading = filesRef.current.some((f) => f.status === 'uploading')
      if ((!text && !attached.length) || sending || uploading) return
      setInput('')
      // The sent files move into the message (keeping their previews); refused ones are dropped.
      for (const f of filesRef.current) if (f.status === 'error' && f.previewUrl) URL.revokeObjectURL(f.previewUrl)
      updateFiles(() => [])
      const pendingId = nextId()
      setMessages((m) => [
        ...m,
        {
          id: nextId(),
          role: 'user',
          content: text,
          attachments: attached.length
            ? attached.map((f) => ({
                id: f.serverId as string,
                name: f.name,
                kind: f.kind,
                sizeBytes: f.size,
                pages: f.pages,
                localUrl: f.previewUrl,
              }))
            : undefined,
        },
        { id: pendingId, role: 'assistant', content: '', pending: true },
      ])
      setSending(true)
      try {
        if (resumeRef.current) await resumeRef.current
        const data = await postTurn(
          text,
          attached.map((f) => f.serverId as string),
        )
        const reply = data?.assistant_reply || '…'
        patchMessage(pendingId, {
          pending: false,
          content: reply,
          serverId: data?.message_id || undefined,
          // Proposed changes: nothing runs until the staff member confirms.
          actions: chatCards(data?.confirm_actions, Date.now()),
        })
        // Voice mode reads the reply back without a tap.
        if (autoVoiceRef.current && caps.voiceOut && mountedRef.current && reply.length <= MAX_AUTO_SPEAK) {
          speak(pendingId, reply)
        }
        // The legacy `navigate` field is for old bundles and is ignored here.
        if (data?.ui_action && mountedRef.current) {
          const result = await runUiAction(router, data.ui_action, uiActionContext())
          patchMessage(pendingId, {
            note:
              result.outcome === 'navigated'
                ? { tone: 'ok', text: `Opened ${result.target.label}` }
                : { tone: 'muted', text: refusalNote(result.reason) },
          })
        }
      } catch (e) {
        if (hideIfDisabled(e)) return
        const code = errorCode(e)
        // Refused before anything was stored: the files go back in the composer.
        if (attached.length && code === 'ai_turn_in_progress') updateFiles((prev) => [...attached, ...prev])
        patchMessage(pendingId, { pending: false, system: true, content: turnErrorText(code) })
      } finally {
        setSending(false)
        if (!autoVoiceRef.current) focusInput()
      }
    },
    [
      sending,
      postTurn,
      patchMessage,
      router,
      uiActionContext,
      hideIfDisabled,
      focusInput,
      caps.voiceOut,
      speak,
      updateFiles,
    ],
  )

  // ── Confirm cards: /ai/actions/:id/{confirm,undo,reject} ──────────────────
  // Cards with a request in flight; a second tap is ignored.
  const cardOpsRef = useRef(new Set<string>())

  /** A change (or its undo) landed: refresh the screens it touched. */
  const refreshDomains = useCallback(
    (card: AIActionCard) => {
      if (CHANGED_STATUSES.has(card.status)) invalidateActionDomains(queryClient, card.card?.domains)
    },
    [queryClient],
  )

  /** Re-reads one card from the transcript when a refusal left its state unknown. */
  const reloadCard = useCallback(
    async (messageId: string, cardId: string) => {
      const conv = convIdRef.current
      if (!conv) return
      try {
        const res = await apiClient.aiGetConversation(conv)
        const fresh = (res.data?.messages ?? []).flatMap((m) => m.actions ?? []).find((c) => c.id === cardId)
        if (!fresh) return
        patchCard(messageId, cardId, toChatCard(fresh))
        refreshDomains(fresh)
      } catch {
        /* the line under the card already says what went wrong */
      }
    },
    [patchCard, refreshDomains],
  )

  const actOnCard = useCallback(
    async (messageId: string, cardId: string, op: CardOp) => {
      if (cardOpsRef.current.has(cardId)) return
      cardOpsRef.current.add(cardId)
      patchCard(messageId, cardId, { busy: op, problem: undefined })
      try {
        if (op === 'reject') {
          await apiClient.aiRejectAction(cardId)
          patchCard(messageId, cardId, { status: 'rejected', busy: undefined })
          return
        }
        const res = op === 'confirm' ? await apiClient.aiConfirmAction(cardId) : await apiClient.aiUndoAction(cardId)
        if (!res.data) throw new Error('ai: no card in the response')
        // The server's copy replaces ours, with a fresh clock offset.
        patchCard(messageId, cardId, { ...toChatCard(res.data), busy: undefined, problem: undefined })
        refreshDomains(res.data)
      } catch (e) {
        if (hideIfDisabled(e)) return
        const outcome = cardErrorOutcome(op, errorCode(e), e instanceof ApiClientError ? e.message : undefined)
        patchCard(messageId, cardId, { ...outcome.patch, busy: undefined, problem: outcome.problem })
        if (outcome.refresh) void reloadCard(messageId, cardId)
      } finally {
        cardOpsRef.current.delete(cardId)
      }
    },
    [patchCard, refreshDomains, reloadCard, hideIfDisabled],
  )

  const openCardScreen = useCallback(
    async (open: AIActionCardOpen): Promise<string | null> => {
      // Validated against the nav registry exactly like a turn's ui_action.
      const result = await runUiAction(
        router,
        {
          type: 'navigate',
          target: open.target,
          route: open.route,
          search: open.search ?? {},
          click_target: null,
          label: open.label,
        },
        uiActionContext(),
      )
      return result.outcome === 'navigated' ? null : refusalNote(result.reason)
    },
    [router, uiActionContext],
  )

  const onFeedback = useCallback(
    (m: ChatMessage, thumbs: 1 | -1) => {
      if (!m.serverId) return
      patchMessage(m.id, { feedback: thumbs })
      apiClient.aiSubmitFeedback({ message_id: m.serverId, thumbs }).catch((e: unknown) => {
        if (hideIfDisabled(e)) return
        patchMessage(m.id, { feedback: undefined })
      })
    },
    [patchMessage, hideIfDisabled],
  )

  // ── Report a problem ──────────────────────────────────────────────────────
  const submitReport = useCallback(
    async (raw: string) => {
      const text = raw.trim()
      if (!text || sending) return
      setInput('')
      setReportMode(false)
      const noteId = nextId()
      setMessages((m) => [
        ...m,
        { id: nextId(), role: 'user', content: text },
        { id: noteId, role: 'assistant', content: '', pending: true },
      ])
      setSending(true)
      try {
        await apiClient.aiReportIssue({
          description: text,
          screen_route: window.location.pathname,
          app_version: await appVersion(),
          recent_errors: getRecentApiErrors(),
        })
        patchMessage(noteId, {
          pending: false,
          system: true,
          content: 'Thanks — your report has been sent to the team.',
        })
      } catch (e) {
        if (hideIfDisabled(e)) return
        const notConfigured = errorCode(e) === 'field_report_not_configured'
        if (notConfigured) setCaps({ report: false })
        patchMessage(noteId, {
          pending: false,
          system: true,
          content: notConfigured
            ? "Problem reporting isn't set up for this restaurant yet."
            : "Couldn't send the report just now — please try again in a moment.",
        })
      } finally {
        setSending(false)
        focusInput()
      }
    },
    [sending, patchMessage, hideIfDisabled, setCaps, focusInput],
  )

  const submit = useCallback(() => {
    if (reportMode) void submitReport(input)
    else void send(input)
  }, [reportMode, submitReport, send, input])

  const seed = useCallback(
    (text: string) => {
      setReportMode(false)
      setInput(text)
      focusInput()
    },
    [focusInput],
  )

  // ── Voice input: mic → /ai/transcribe ─────────────────────────────────────
  const [isRecording, setIsRecording] = useState(false)
  const [recordingSeconds, setRecordingSeconds] = useState(0)
  const [transcribing, setTranscribing] = useState(false)
  const [micError, setMicError] = useState<string | null>(null)
  const [level, setLevel] = useState(0)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const audioCtxRef = useRef<AudioContext | null>(null)
  const levelRafRef = useRef(0)

  const releaseMic = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
    recorderRef.current = null
    cancelAnimationFrame(levelRafRef.current)
    void audioCtxRef.current?.close().catch(() => {})
    audioCtxRef.current = null
    setLevel(0)
  }, [])

  const transcribe = useCallback(
    async (blob: Blob) => {
      if (!mountedRef.current) return
      setTranscribing(true)
      try {
        const res = await apiClient.aiTranscribe(blob)
        const text = (res.data?.text ?? '').trim()
        if (!mountedRef.current) return
        if (!text) {
          setMicError("I didn't catch that — please try again")
        } else if (sendingRef.current) {
          // A turn is still in flight: keep the words rather than drop them.
          setInput((prev) => (prev.trim() ? `${prev.trim()} ${text}` : text))
          focusInput()
        } else {
          // Sent as spoken, in the bubble too, not pasted for a second tap;
          // anything typed before the recording leads.
          const typed = inputValueRef.current.trim()
          void send(typed ? `${typed} ${text}` : text)
        }
      } catch (e) {
        if (hideIfDisabled(e)) return
        if (errorCode(e) === 'ai_voice_unavailable') {
          setCaps({ voiceIn: false })
          setMicError("Voice input isn't set up for this restaurant.")
        } else {
          setMicError("I couldn't make that out — please try again")
        }
      } finally {
        if (mountedRef.current) setTranscribing(false)
      }
    },
    [hideIfDisabled, setCaps, focusInput, send],
  )

  const startRecording = useCallback(async () => {
    setMicError(null)
    if (!navigator.mediaDevices?.getUserMedia) {
      setMicError('No microphone on this device')
      return
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      if (!mountedRef.current) {
        stream.getTracks().forEach((t) => t.stop())
        return
      }
      stopPlayback()
      streamRef.current = stream
      chunksRef.current = []
      const mr = new MediaRecorder(stream)
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data)
      }
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' })
        chunksRef.current = []
        releaseMic()
        if (blob.size > 0) void transcribe(blob)
      }
      recorderRef.current = mr
      mr.start()
      setRecordingSeconds(0)
      setIsRecording(true)
      // Mic level for the voice-mode orb. Best effort: a browser without
      // AudioContext just gets a still orb.
      try {
        const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
        if (Ctx) {
          const ctx = new Ctx()
          audioCtxRef.current = ctx
          const analyser = ctx.createAnalyser()
          analyser.fftSize = 512
          ctx.createMediaStreamSource(stream).connect(analyser)
          const buf = new Uint8Array(analyser.frequencyBinCount)
          const tick = () => {
            analyser.getByteTimeDomainData(buf)
            let sum = 0
            for (const v of buf) {
              const d = (v - 128) / 128
              sum += d * d
            }
            setLevel(Math.min(1, Math.sqrt(sum / buf.length) * 4))
            levelRafRef.current = requestAnimationFrame(tick)
          }
          levelRafRef.current = requestAnimationFrame(tick)
        }
      } catch {
        /* no level, no problem */
      }
    } catch {
      releaseMic()
      setMicError('Microphone access is needed')
    }
  }, [releaseMic, transcribe, stopPlayback])

  const stopRecording = useCallback(() => {
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop()
    setIsRecording(false)
  }, [])

  useEffect(() => {
    if (!isRecording) return
    const id = window.setInterval(() => setRecordingSeconds((s) => s + 1), 1000)
    return () => window.clearInterval(id)
  }, [isRecording])

  useEffect(() => {
    if (isRecording && recordingSeconds >= MAX_RECORDING_SECONDS) stopRecording()
  }, [isRecording, recordingSeconds, stopRecording])

  // Release the mic and stop playback if the surface unmounts mid-way.
  useEffect(() => {
    return () => {
      if (recorderRef.current?.state === 'recording') {
        recorderRef.current.onstop = null
        recorderRef.current.stop()
      }
      streamRef.current?.getTracks().forEach((t) => t.stop())
      cancelAnimationFrame(levelRafRef.current)
      void audioCtxRef.current?.close().catch(() => {})
      audioRef.current?.pause()
      if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current)
      deviceStopRef.current?.()
    }
  }, [])

  const newChat = useCallback(() => {
    resumeSupersededRef.current = true
    resumeRef.current = null
    setConvId(null)
    writeStoredConversation(userId, null)
    setMessages([])
    setReportMode(false)
    stopPlayback()
    focusInput()
  }, [userId, setConvId, stopPlayback, focusInput])

  const openConversation = useCallback(
    async (id: string) => {
      if (id === convIdRef.current) return
      resumeSupersededRef.current = true
      resumeRef.current = null
      stopPlayback()
      setReportMode(false)
      try {
        const res = await apiClient.aiGetConversation(id)
        if (!mountedRef.current) return
        setConvId(id)
        writeStoredConversation(userId, id)
        setMessages(restoredMessages(res.data?.messages))
        focusInput()
      } catch (e) {
        if (hideIfDisabled(e)) return
        setMessages([
          { id: nextId(), role: 'assistant', system: true, content: 'That chat is no longer available.' },
        ])
      }
    },
    [userId, setConvId, stopPlayback, focusInput, hideIfDisabled],
  )

  const filesReady = files.some((f) => f.status === 'ready')
  const filesUploading = files.some((f) => f.status === 'uploading')
  const canSend =
    !sending &&
    !isRecording &&
    (reportMode ? !!input.trim() : (!!input.trim() || filesReady) && !filesUploading)

  return {
    messages,
    input,
    setInput,
    sending,
    canSend,
    reportMode,
    setReportMode,
    pendingLabel: PENDING_LABELS[pendingLabelIdx],
    inputRef,
    scrollRef,
    focusInput,
    conversationId,
    send,
    submit,
    seed,
    newChat,
    openConversation,
    onFeedback,
    actOnCard,
    openCardScreen,
    voice: { isRecording, recordingSeconds, transcribing, micError, level, startRecording, stopRecording },
    speech: { voiceState, onSpeak, stopPlayback },
    autoVoice,
    setAutoVoice,
    files,
    addFiles,
    removeFile,
  }
}

/** Voice mode reads replies up to this long; longer ones stay on screen. */
const MAX_AUTO_SPEAK = 1500
